import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Hooks } from "@xt/xtbase";
import step7Img from "@/assets/img/cfd-guide/guide1.png";
import lineImg from "@/assets/img/cfd-guide/line.png";
import { useCfdGuideStageRect } from "./useCfdGuideStageRect";
import styles from "./StandardGuideStep7.module.scss";

const { useTranslation } = Hooks;

export type StandardGuideStep7Props = {
  onSkip: () => void;
  onNext: () => void;
};

const DESIGN_WIDTH = 375;

function getScale(): number {
  if (typeof window === "undefined") return 1;
  const w = window.innerWidth || DESIGN_WIDTH;
  return w / DESIGN_WIDTH;
}

/** 与 positionList `viewOrderBtn` 内 history 图标一致（16×16 viewBox） */
function HistoryIconOverlay() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1" y="1" width="14" height="14" rx="2.46094" stroke="white" strokeWidth="1.7" />
      <path d="M5 6H11" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M5 10H11" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

const StandardGuideStep7: React.FC<StandardGuideStep7Props> = ({ onSkip, onNext }) => {
  const t = useTranslation();

  const scale = getScale();

  const imgSrc = (step7Img as any)?.src || (step7Img as any);
  const lineImgSrc = (lineImg as any)?.src || (lineImg as any);

  const anchor = useCfdGuideStageRect('[data-guide-target="historyButton"]');

  const gapBelow = 58 * scale;
  const tipWidthDesign = 247 * scale;

  const tipLayout = useMemo(() => {
    if (!anchor || typeof window === "undefined") return null;
    const vw = window.innerWidth;
    const pad = 16;
    const anchorCx = anchor.left + anchor.width / 2;
    const tipWidth = Math.min(tipWidthDesign, vw - 2 * pad);
    let tipLeft = anchorCx - tipWidth / 2;
    tipLeft = Math.max(pad, Math.min(tipLeft, vw - tipWidth - pad));
    const lineCenterInTip = anchorCx - tipLeft;
    return { tipLeft, lineCenterInTip, tipWidth };
  }, [anchor, tipWidthDesign]);

  return (
    <div style={{ ["--sg-scale" as any]: scale }}>
      {anchor && tipLayout ? (
        <>
          <div
            className={styles.highlightRect}
            style={{
              position: "fixed",
              left: anchor.left,
              top: anchor.top,
              width: anchor.width,
              height: anchor.height,
              zIndex: 10001,
            }}
          >
            <div className={styles.historyBtn}>
              <HistoryIconOverlay />
            </div>
          </div>

          <div
            className={styles.tip}
            style={{
              position: "fixed",
              left: tipLayout.tipLeft,
              top: anchor.top + anchor.height + gapBelow,
              width: tipLayout.tipWidth,
              zIndex: 10001,
            }}
          >
            <div className={styles.tipBg}>
              <div className={styles.tipText}>
                <div className={styles.tipTitle}>{t("cfd.guide.standard.step7.title")}</div>
                <div className={styles.tipDesc}>{t("cfd.guide.standard.step7.desc")}</div>
              </div>
              <img className={styles.tipImg} src={imgSrc} alt="" />
            </div>
            <div className={styles.star1} />
            <div className={styles.star2} />
            <img
              className={styles.lineImg}
              src={lineImgSrc}
              alt=""
              style={{
                left: tipLayout.lineCenterInTip,
                transform: "translateX(-50%)",
              }}
            />
          </div>
        </>
      ) : null}

      <div className={styles.btnGroup}>
        <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={onNext}>
          {`${t("cfd.guide.next")} 7/7`}
        </button>
        <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={onSkip}>
          {t("cfd.guide.skip")}
        </button>
      </div>
    </div>
  );
};

export default observer(StandardGuideStep7);
